export type PushSubscriptionInsert = {
  profile_id: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  user_agent?: string | null;
  enabled?: boolean; // when omitted, server default or existing value should remain
};

export type SaveSubscriptionParams = {
  supabaseUrl?: string;
  anonKey?: string;
  accessToken?: string; // Supabase auth access_token (jwt), required by RLS
  record: PushSubscriptionInsert;
};

export type SaveResult =
  | { ok: true; upserted: any }
  | { ok: false; reason: string; status?: number; error?: unknown };

/**
 * Upserts into public.push_subscriptions, honoring unique(endpoint) and RLS (profile_id must equal auth.uid()).
 * Requires: Authorization Bearer <accessToken> for an authenticated user; anon key as apikey header.
 */
export async function savePushSubscription(params: SaveSubscriptionParams): Promise<SaveResult> {
  const { supabaseUrl, anonKey, accessToken, record } = params;
  if (!supabaseUrl || !anonKey) {
    return { ok: false, reason: 'supabase_not_configured' };
  }
  if (!accessToken || !record.profile_id) {
    return { ok: false, reason: 'auth_required' };
  }
  try {
    const url = new URL(`${supabaseUrl.replace(/\/+$/, '')}/rest/v1/push_subscriptions`);
    url.searchParams.set('on_conflict', 'endpoint');
    const payload: any = {
      profile_id: record.profile_id,
      endpoint: record.endpoint,
      p256dh: record.p256dh,
      auth: record.auth,
      user_agent: record.user_agent ?? navigator.userAgent
    };
    if (typeof record.enabled === 'boolean') {
      payload.enabled = record.enabled;
    }
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${accessToken}`,
        Prefer: 'return=representation,resolution=merge-duplicates'
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, reason: text || res.statusText, status: res.status };
    }
    const data = (await res.json()) as any[];
    return { ok: true, upserted: data[0] ?? null };
  } catch (err) {
    return { ok: false, reason: 'network_error', error: err };
  }
}

